import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Shield, ShieldCheck, ShieldOff } from 'lucide-react';

function merge2faView(apiStatus, authUser) {
    const activeMethod = apiStatus?.active_method || apiStatus?.method || authUser?.twofa_method || null;
    const enabled = Boolean(apiStatus?.enabled || authUser?.twofa_method);
    return {
        enabled,
        active_method: activeMethod,
        method: activeMethod,
        available: Boolean(apiStatus?.available ?? authUser?.can_enable_2fa),
    };
}

function TotpSettings({ view, onRefresh }) {
    const { refresh } = useAuth();
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState('');
    const [disablePassword, setDisablePassword] = useState('');
    const [disableCode, setDisableCode] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const isActive = view.enabled && view.active_method === 'totp';

    const startSetup = async () => {
        setError(''); setBusy(true);
        try {
            const { data } = await api.post('/auth/2fa/setup');
            setSetup(data);
            setCode('');
        } catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const enable = async () => {
        setError(''); setBusy(true);
        try {
            await api.post('/auth/2fa/enable', { code });
            setSetup(null); setCode('');
            await onRefresh();
            await refresh({ silent: true });
        } catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const disable = async () => {
        setError(''); setBusy(true);
        try {
            await api.post('/auth/2fa/disable', { password: disablePassword, code: disableCode });
            setDisablePassword(''); setDisableCode('');
            await onRefresh();
            await refresh({ silent: true });
        } catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0, lineHeight: 1.5 }}>
                Google Authenticator sau Authy — cod de 6 cifre la fiecare login.
            </p>
            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>}
            {!isActive && !setup && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-primary btn-sm" onClick={startSetup} disabled={busy}>Activează Authenticator</button>
                </div>
            )}
            {setup && (
                <div style={{ marginTop: '0.8rem' }}>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Scanează QR-ul, apoi introdu codul.</p>
                    <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                        <div style={{ background: '#fff', padding: 12, borderRadius: 12, display: 'inline-block' }}>
                            <img src={setup.qr_base64} alt="QR 2FA" style={{ width: 180, height: 180, display: 'block' }} />
                        </div>
                    </div>
                    <input className="input" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
                           onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                           style={{ letterSpacing: '0.35em', textAlign: 'center', fontSize: '1.2rem', marginBottom: '0.8rem' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setSetup(null); setCode(''); }}>Anulează</button>
                        <button type="button" className="btn btn-primary btn-sm" disabled={busy || code.length < 6} onClick={enable}>Confirmă</button>
                    </div>
                </div>
            )}
            {isActive && (
                <div style={{ marginTop: '0.8rem' }}>
                    <input className="input" type="password" placeholder="Parola curentă" value={disablePassword}
                           onChange={e => setDisablePassword(e.target.value)} style={{ marginBottom: 8 }} />
                    <input className="input" inputMode="numeric" maxLength={6} placeholder="Cod Authenticator" value={disableCode}
                           onChange={e => setDisableCode(e.target.value.replace(/\D/g, ''))}
                           style={{ letterSpacing: '0.35em', textAlign: 'center', marginBottom: 8 }} />
                    <button type="button" className="btn btn-danger btn-sm" disabled={busy || !disablePassword || disableCode.length < 6} onClick={disable}>
                        <ShieldOff size={14} /> Dezactivează Authenticator
                    </button>
                </div>
            )}
        </>
    );
}

export function TwoFactorSettings() {
    const { user } = useAuth();
    const [apiStatus, setApiStatus] = useState({});

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/2fa/status');
            setApiStatus(data);
        } catch {
            // user.twofa_method din AuthContext acoperă afișarea
        }
    }, []);

    useEffect(() => { loadStatus(); }, [user?.id, loadStatus]);

    const view = useMemo(() => merge2faView(apiStatus, user), [apiStatus, user]);

    if (!view.available && !user?.can_enable_2fa) return null;

    return (
        <div className="panel" style={{ maxWidth: 640, marginBottom: '1.4rem' }} data-testid="twofa-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.6rem' }}>
                {view.enabled ? <ShieldCheck size={18} color="var(--neon-teal)" /> : <Shield size={18} color="var(--amber)" />}
                <h2 className="headline" style={{ fontSize: '1.35rem', margin: 0 }}>
                    Autentificare în doi pași
                </h2>
                <span className={`badge badge-${view.enabled ? 'teal' : 'muted'}`} data-testid="twofa-badge">
                    {view.enabled ? 'Activ — Authenticator' : 'Inactiv'}
                </span>
            </div>

            <TotpSettings view={view} onRefresh={loadStatus} />
        </div>
    );
}
